import { useEffect, useRef } from "react";

interface PagedQuery<Page> {
  data?: { pages: Page[] };
  hasNextPage: boolean;
  isFetching: boolean;
  fetchNextPage: () => unknown;
}

/** Unattended rounds per filter; after that the manual Load more takes over. */
const MAX_AUTO_ROUNDS = 10;

/**
 * A server page can be empty while more rows remain: list-users post-filters
 * status / ageBand over the pages it scans, and pageQuery's contract is that
 * callers keep paging until nextCursor is null and never stop on an empty
 * page. Rendering "No matching entries" with a manual Load more on such a
 * page reads as "no disabled users" when there are some further down, so
 * this keeps fetching while the newest page is empty and a cursor remains,
 * for at most MAX_AUTO_ROUNDS callables so a rare band on a large
 * collection cannot sweep it unattended. `rowsOf` counts a page's rows.
 */
export function useAutoFillPages<Page>(query: PagedQuery<Page>, rowsOf: (page: Page) => number): void {
  const pages = query.data?.pages;
  const lastPage = pages?.at(-1);
  const lastRows = lastPage === undefined ? undefined : rowsOf(lastPage);
  const { hasNextPage, isFetching, fetchNextPage } = query;
  const rounds = useRef(0);
  if (pages?.length === 1) rounds.current = 0; // a new filter: fresh budget
  useEffect(() => {
    if (lastRows !== 0 || !hasNextPage || isFetching) return;
    if (rounds.current >= MAX_AUTO_ROUNDS) return;
    rounds.current++;
    void fetchNextPage();
  }, [lastRows, hasNextPage, isFetching, fetchNextPage]);
}
